import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Hiring v3 / Ticket D2 — Interview script panel.
// Lazy-loads the per-application question script + a picker modal that
// pulls active questions from the pool. Cross-manager visible per §12.4.
export function InterviewScriptPanel({ applicationId, headers, applicantRole }) {
  const [script, setScript] = useState([]);
  const [responses, setResponses] = useState([]); // all reviewers' responses
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pool, setPool] = useState([]);
  const [poolLoading, setPoolLoading] = useState(false);
  const [axisFilter, setAxisFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState(applicantRole || '');
  const [savingMsg, setSavingMsg] = useState('');
  // Per-question expanded state (captures response inline)
  const [expandedQid, setExpandedQid] = useState(null);
  const [responseDraft, setResponseDraft] = useState({}); // { qid: { response_text, scored_rating, notes } }

  async function loadScript() {
    setLoading(true);
    try {
      const [scriptR, respR] = await Promise.all([
        api('GET', `/api/admin/applications/${applicationId}/interview-script`, null, headers),
        api('GET', `/api/admin/applications/${applicationId}/interview-responses`, null, headers),
      ]);
      setScript(Array.isArray(scriptR?.script) ? scriptR.script : []);
      setResponses(Array.isArray(respR?.responses) ? respR.responses : []);
    } catch (_) { setScript([]); setResponses([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadScript(); }, [applicationId]);

  // Hydrate the inline draft from the current user's existing response (if any).
  function getMyResponse(qid) {
    const myUid = Number(headers['x-user-id']);
    return responses.find(r => r.question_id === qid && Number(r.reviewer_user_id) === myUid) || null;
  }
  function ensureDraft(qid, scriptRow) {
    if (responseDraft[qid]) return;
    const mine = getMyResponse(qid);
    setResponseDraft(d => ({ ...d, [qid]: {
      response_text: mine?.response_text || '',
      scored_rating: mine?.scored_rating || '',
      scored_axis: mine?.scored_axis || (scriptRow?.primary_axis || ''),
      notes: mine?.notes || '',
    } }));
  }
  async function saveResponse(qid) {
    const d = responseDraft[qid];
    if (!d) return;
    setSavingMsg('Saving response…');
    try {
      const payload = { question_id: qid };
      if (d.response_text && d.response_text.trim()) payload.response_text = d.response_text.trim();
      if (d.scored_rating) payload.scored_rating = d.scored_rating;
      if (d.scored_axis) payload.scored_axis = d.scored_axis;
      if (d.notes && d.notes.trim()) payload.notes = d.notes.trim();
      await api('POST', `/api/admin/applications/${applicationId}/interview-responses`, payload, headers);
      setSavingMsg('Saved.');
      setTimeout(() => setSavingMsg(''), 1200);
      await loadScript();
    } catch (e) { setSavingMsg(`Failed: ${e.message || e}`); }
  }

  async function loadPool() {
    setPoolLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', 'active');
      if (axisFilter) params.set('axis', axisFilter);
      const r = await api('GET', `/api/admin/interview-questions?${params.toString()}`, null, headers);
      let qs = Array.isArray(r?.questions) ? r.questions : [];
      // Client-side stage + role filtering since those are JSONB arrays.
      if (stageFilter) qs = qs.filter(q => Array.isArray(q.stages) && q.stages.includes(stageFilter));
      if (roleFilter) qs = qs.filter(q =>
        !Array.isArray(q.roles) || q.roles.length === 0
        || q.roles.includes('all') || q.roles.includes(roleFilter)
      );
      setPool(qs);
    } catch (_) { setPool([]); }
    finally { setPoolLoading(false); }
  }
  useEffect(() => { if (pickerOpen) loadPool(); }, [pickerOpen, axisFilter, stageFilter, roleFilter]);

  async function addQuestion(qid) {
    setSavingMsg('Adding…');
    try {
      await api('POST', `/api/admin/applications/${applicationId}/interview-script`,
        { question_id: qid }, headers);
      setSavingMsg('');
      await loadScript();
    } catch (e) { setSavingMsg(`Failed: ${e.message || e}`); }
  }
  async function removeQuestion(qid) {
    try {
      await api('DELETE', `/api/admin/applications/${applicationId}/interview-script/${qid}`, null, headers);
      await loadScript();
    } catch (e) { alert(e.message || String(e)); }
  }

  const scriptIds = new Set(script.map(s => s.question_id));

  const AXIS_OPTIONS = [
    'awareness', 'hospitality', 'collaboration', 'learning_posture',
    'calm_under_pressure', 'communication_quality', 'logistics_realism',
    'background', 'role_specific', 'curveball',
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
          Interview script {script.length > 0 && <span style={{ color: '#888', fontWeight: 400 }}>({script.length})</span>}
        </h3>
        <button onClick={() => setPickerOpen(o => !o)}
          style={{ fontSize: 12, padding: '4px 12px', border: '1px solid var(--gold, #c89b3c)',
                    background: pickerOpen ? '#fef9ee' : '#fff', color: '#212121',
                    borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
          {pickerOpen ? 'Close picker' : '+ Add questions'}
        </button>
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Loading…</div>
      ) : script.length === 0 ? (
        <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
          No questions selected yet. Pick from the active pool — visible to all managers reviewing this app.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          {script.map((s, i) => {
            const qResponses = responses.filter(r => r.question_id === s.question_id);
            const expanded = expandedQid === s.question_id;
            const draft = responseDraft[s.question_id] || {};
            return (
            <div key={s.question_id} style={{ border: '1px solid #e0e0e0', borderRadius: 6,
                                                padding: 10, fontSize: 13, background: '#fafafa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ color: '#888', fontSize: 11, marginRight: 6 }}>{i + 1}.</span>
                  <strong style={{ fontWeight: 600 }}>{s.text}</strong>
                  {s.primary_axis && (
                    <span style={{ marginLeft: 8, fontSize: 10, color: '#7a5b1a',
                                    background: '#fff8e6', border: '1px solid #f0d9a3',
                                    padding: '1px 6px', borderRadius: 4 }}>
                      {s.primary_axis.replace(/_/g, ' ')}
                    </span>
                  )}
                  {qResponses.length > 0 && (
                    <span style={{ marginLeft: 6, fontSize: 10, color: '#1e7d3a',
                                    background: '#e6f4ea', border: '1px solid #c8e2c8',
                                    padding: '1px 6px', borderRadius: 4 }}>
                      {qResponses.length} response{qResponses.length === 1 ? '' : 's'}
                    </span>
                  )}
                  {(s.strong_signals || s.interviewer_notes) && (
                    <details style={{ marginTop: 6, fontSize: 12, color: '#555' }}>
                      <summary style={{ cursor: 'pointer', color: '#888', fontSize: 11 }}>Signals & notes</summary>
                      {s.strong_signals && <div style={{ marginTop: 4 }}><strong>Strong:</strong> {s.strong_signals}</div>}
                      {s.weak_signals && <div style={{ marginTop: 2 }}><strong>Weak:</strong> {s.weak_signals}</div>}
                      {s.interviewer_notes && <div style={{ marginTop: 2 }}>{s.interviewer_notes}</div>}
                    </details>
                  )}
                  <div style={{ marginTop: 4, fontSize: 10, color: '#9e9e9e' }}>
                    Added by {s.added_by_name || 'a manager'} · {s.added_at ? new Date(s.added_at).toLocaleDateString() : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                  <button onClick={() => {
                    if (expanded) { setExpandedQid(null); }
                    else { ensureDraft(s.question_id, s); setExpandedQid(s.question_id); }
                  }}
                    style={{ fontSize: 11, padding: '2px 10px', border: '1px solid var(--gold, #c89b3c)',
                              borderRadius: 4, fontWeight: 600,
                              background: expanded ? '#fef9ee' : 'var(--cream, #fef9ee)',
                              color: '#212121', cursor: 'pointer' }}>
                    {expanded ? 'Close' : 'Capture'}
                  </button>
                  <button onClick={() => removeQuestion(s.question_id)} title="Remove from script"
                    style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #d0d4d9',
                              borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#666' }}>
                    Remove
                  </button>
                </div>
              </div>

              {/* Existing responses from any reviewer — rendered above the form. */}
              {qResponses.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {qResponses.map(r => (
                    <div key={r.id} style={{ borderLeft: '3px solid #c8e2c8', paddingLeft: 8, fontSize: 12 }}>
                      <div style={{ fontSize: 10, color: '#9e9e9e' }}>
                        <strong>{r.reviewer_name || 'Manager'}</strong>
                        {r.scored_rating && <> · rated <strong>{r.scored_rating}</strong></>}
                        {r.scored_axis && r.scored_axis !== s.primary_axis && <> on {r.scored_axis.replace(/_/g, ' ')}</>}
                        {' · '}{new Date(r.updated_at || r.created_at).toLocaleDateString()}
                      </div>
                      {r.response_text && <div style={{ marginTop: 2 }}>{r.response_text}</div>}
                      {r.notes && <div style={{ marginTop: 2, color: '#666', fontStyle: 'italic' }}>{r.notes}</div>}
                    </div>
                  ))}
                </div>
              )}

              {expanded && (
                <div style={{ marginTop: 10, padding: 10, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6 }}>
                  <label style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 4 }}>
                    What did they say?
                  </label>
                  <textarea value={draft.response_text || ''}
                    onChange={e => setResponseDraft(d => ({ ...d, [s.question_id]: { ...(d[s.question_id] || {}), response_text: e.target.value } }))}
                    rows={3}
                    style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8,
                              fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: '#555' }}>Rate this response:</span>
                    {['strong','ok','weak'].map(r => (
                      <label key={r} style={{ fontSize: 11, padding: '2px 10px', border: '1px solid #d0d4d9',
                                                borderRadius: 99, cursor: 'pointer',
                                                background: draft.scored_rating === r ? 'var(--gold)' : '#fff',
                                                color: draft.scored_rating === r ? '#fff' : '#333' }}>
                        <input type="radio" name={`r-${s.question_id}`} checked={draft.scored_rating === r}
                          style={{ display: 'none' }}
                          onChange={() => setResponseDraft(d => ({ ...d, [s.question_id]: { ...(d[s.question_id] || {}), scored_rating: r } }))} />
                        {r}
                      </label>
                    ))}
                    {draft.scored_rating && (
                      <button onClick={() => setResponseDraft(d => ({ ...d, [s.question_id]: { ...(d[s.question_id] || {}), scored_rating: '' } }))}
                        style={{ fontSize: 11, color: '#888', background: 'none', border: 0, cursor: 'pointer' }}>
                        clear
                      </button>
                    )}
                  </div>
                  <label style={{ fontSize: 11, color: '#555', display: 'block', marginTop: 8, marginBottom: 4 }}>
                    Notes (private to managers)
                  </label>
                  <input type="text" value={draft.notes || ''}
                    onChange={e => setResponseDraft(d => ({ ...d, [s.question_id]: { ...(d[s.question_id] || {}), notes: e.target.value } }))}
                    placeholder="Optional follow-ups, observations…"
                    style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 6,
                              fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => saveResponse(s.question_id)}
                      style={{ fontSize: 12, padding: '4px 14px', border: 0,
                                borderRadius: 6, background: 'var(--gold)', color: '#fff',
                                fontWeight: 600, cursor: 'pointer' }}>
                      Save response
                    </button>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {pickerOpen && (
        <div style={{ marginTop: 10, padding: 12, border: '1px solid #e0e0e0',
                      borderRadius: 8, background: '#fff' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            <select value={axisFilter} onChange={e => setAxisFilter(e.target.value)}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 12 }}>
              <option value="">Any axis</option>
              {AXIS_OPTIONS.map(ax => <option key={ax} value={ax}>{ax.replace(/_/g, ' ')}</option>)}
            </select>
            <select value={stageFilter} onChange={e => setStageFilter(e.target.value)}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 12 }}>
              <option value="">Any stage</option>
              <option value="phone_screen">Phone screen</option>
              <option value="in_person">In person</option>
            </select>
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 12 }}>
              <option value="">Any role</option>
              <option value="Barista">Barista</option>
              <option value="Shift Lead">Shift Lead</option>
              <option value="Coffee Roaster">Coffee Roaster</option>
              <option value="Kitchen">Kitchen</option>
              <option value="Asst Manager">Asst Manager</option>
              <option value="Manager">Manager</option>
            </select>
            {savingMsg && <span style={{ fontSize: 11, color: savingMsg.startsWith('Failed') ? '#b00020' : '#666', alignSelf: 'center' }}>{savingMsg}</span>}
          </div>
          {poolLoading ? (
            <div style={{ fontSize: 12, color: '#888' }}>Loading…</div>
          ) : pool.length === 0 ? (
            <div style={{ fontSize: 12, color: '#666', padding: '8px 4px' }}>
              No active questions match the current filters.
              {(axisFilter || stageFilter || roleFilter) && (
                <span> Try clearing the filters above, or </span>
              )}
              <span> activate questions in <strong>Settings → Hiring → Interview questions</strong>.</span>
            </div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {pool.map(q => {
                const already = scriptIds.has(q.id);
                return (
                  <div key={q.id} style={{ display: 'flex', justifyContent: 'space-between',
                                            alignItems: 'flex-start', gap: 10, padding: 8,
                                            borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ color: '#888', fontSize: 10, marginRight: 6 }}>{q.external_id}</span>
                      {q.text}
                      <div style={{ fontSize: 10, color: '#9e9e9e', marginTop: 2 }}>
                        {q.primary_axis && q.primary_axis.replace(/_/g, ' ')}
                        {q.use_count > 0 && ` · used ${q.use_count}×`}
                      </div>
                    </div>
                    <button onClick={() => !already && addQuestion(q.id)} disabled={already}
                      style={{ fontSize: 11, padding: '3px 10px', border: '1px solid var(--gold, #c89b3c)',
                                borderRadius: 4, fontWeight: 600,
                                background: already ? '#f3f4f6' : 'var(--cream, #fef9ee)',
                                color: already ? '#888' : '#212121',
                                cursor: already ? 'default' : 'pointer', flexShrink: 0 }}>
                      {already ? 'Added' : '+ Add'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
